/**
 * UnifiedDashboard - Jeden dashboard dla wszystkich ról
 *
 * Wyświetla odpowiedni content w zależności od roli użytkownika:
 * - Admin/Adminves/Superuser: AdminDashboard content
 * - User/Userves: UserDashboard content (stats, notifications, etc.)
 */

import { getAccessToken } from '../../auth/tokenStore';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import axios from 'axios';
import {
  Clock,
  CircleCheckBig,
  FileText,
  AlertCircle,
  Shield,
  TriangleAlert,
  Zap,
  ChevronRight,
  FilePlus,
  Send,
  CircleX,
  MapPin,
  Calendar,
  Plane,
  Building2,
  GraduationCap,
} from 'lucide-react';

// Import admin dashboard content
import AdminDashboard from '../company/AdminDashboard';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

// Stats grid for the user dashboard: two fixed rows of four cards. Was an auto-playing
// carousel, which hid half the numbers behind a timer.
const StatsGrid = ({ stats, loadingStats, onNavigate, onStatCardClick }) => {
  // Compute unique missing qualifications across all roles
  const uniqueMissingQualifications = React.useMemo(() => {
    if (!stats?.roles_compliance) return [];
    const seen = new Set();
    const result = [];
    for (const role of stats.roles_compliance) {
      for (const mq of role.missing_qualifications || []) {
        if (!seen.has(mq.qualification_type_id)) {
          seen.add(mq.qualification_type_id);
          result.push(mq);
        }
      }
    }
    return result;
  }, [stats?.roles_compliance]);

  // Order IS the layout: the first four fill the top row, the next four the bottom one.
  // Top row = what needs a reaction, bottom row = the running paperwork.
  const statCards = [
    {
      id: 'documents-issues',
      title: 'Documents with Issues',
      value: stats?.certificates?.issues_count || 0,
      icon: AlertCircle,
      iconBg: stats?.certificates?.issues_count > 0 ? 'bg-red-50' : 'bg-slate-50',
      iconColor: stats?.certificates?.issues_count > 0 ? 'text-red-500' : 'text-slate-400',
      onClick: () => onNavigate('/my-documents'),
      highlight: stats?.certificates?.issues_count > 0,
    },
    {
      id: 'missing-qualifications',
      title: 'Missing Qualifications',
      value: uniqueMissingQualifications.length,
      icon: TriangleAlert,
      iconBg: uniqueMissingQualifications.length > 0 ? 'bg-red-50' : 'bg-emerald-50',
      iconColor: uniqueMissingQualifications.length > 0 ? 'text-red-500' : 'text-emerald-500',
      onClick: () => onNavigate('/my-company-roles'),
      highlight: uniqueMissingQualifications.length > 0,
    },
    {
      id: 'total-ppe',
      title: 'My PPE Equipment',
      value: stats?.ppe?.total_count || 0,
      icon: Shield,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-500',
      onClick: () => onNavigate('/my-ppe'),
    },
    {
      id: 'ppe-issues',
      title: 'PPE with Issues',
      value: stats?.ppe?.problematic_count || 0,
      icon: AlertCircle,
      iconBg: stats?.ppe?.problematic_count > 0 ? 'bg-red-50' : 'bg-slate-50',
      iconColor: stats?.ppe?.problematic_count > 0 ? 'text-red-500' : 'text-slate-400',
      onClick: () => onNavigate('/my-ppe'),
      highlight: stats?.ppe?.problematic_count > 0,
    },
    {
      id: 'pending-nominations',
      title: 'Pending Nominations',
      value: stats?.nominations?.pending || 0,
      icon: Clock,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      onClick: () => onNavigate('/my-nominations?status=pending'),
    },
    {
      id: 'active-nominations',
      title: 'Active Nominations',
      value: stats?.nominations?.approved || 0,
      icon: CircleCheckBig,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      onClick: () => onNavigate('/my-nominations?status=approved'),
    },
    {
      id: 'pending-work-permits',
      title: 'Pending Work Permits',
      value: stats?.work_permits?.pending || 0,
      icon: Clock,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      onClick: onStatCardClick,
    },
    {
      id: 'active-work-permits',
      title: 'Active Work Permits',
      value: stats?.work_permits?.active || 0,
      icon: CircleCheckBig,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      onClick: () => onNavigate('/my-workpermits?status=approved'),
    },
    {
      // Hidden for now: a raw document count says nothing about what needs doing, and
      // the row it sat in is the one for problems. Kept whole so restoring it is a
      // one-word change.
      id: 'total-documents',
      hidden: true,
      title: 'My Documents',
      value: stats?.certificates?.total_count || 0,
      icon: FileText,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      onClick: () => onNavigate('/my-documents'),
    },
  ];

  const visibleCards = statCards.filter((card) => !card.hidden);

  const renderStatCard = (card) => {
    const IconComponent = card.icon;
    return (
      <div
        key={card.id}
        className={`bg-white rounded-xl border overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02] ${
          card.highlight ? 'border-red-200 hover:border-red-300' : 'border-slate-200 hover:border-brand-300'
        }`}
        onClick={card.onClick}
      >
        <div className="p-4">
          <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{card.title}</h3>
          <div className="flex items-center justify-between">
            <span className={`text-2xl font-black ${card.highlight ? 'text-red-600' : 'text-slate-900'}`}>
              {loadingStats ? '...' : card.value}
            </span>
            <div className={`h-10 w-10 rounded-xl ${card.iconBg} flex items-center justify-center`}>
              <IconComponent className={`h-5 w-5 ${card.iconColor}`} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Four columns from lg up, so eight cards land as two rows of four; narrower screens
  // stack them rather than shrinking a card until its number is unreadable.
  return (
    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-stats-grid>
      {visibleCards.map(renderStatCard)}
    </div>
  );
};

// User Dashboard Content
const UserDashboardContent = ({ viewMode }) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    nominations: { pending: 0, approved: 0 },
    work_permits: { pending: 0, active: 0 },
    certificates: { total_count: 0, issues_count: 0 },
    ppe: { total_count: 0, problematic_count: 0 },
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoadingStats(true);
        const params = viewMode === 'user' ? { view_mode: 'user' } : {};
        const response = await axios.get(`${API_BASE_URL}/stats/`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
          params,
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, []);

  // Build notifications from stats
  useEffect(() => {
    const dynamicNotifications = [];
    if (stats?.certificates?.expired) {
      stats.certificates.expired.forEach((cert) => {
        dynamicNotifications.push({
          type: 'certificate_expired',
          qualificationTypeId: cert.qualification_type_id || null,
          text: `${cert.name} has expired. Please renew it.`,
        });
      });
    }
    if (stats?.certificates?.expiring) {
      stats.certificates.expiring.forEach((cert) => {
        dynamicNotifications.push({
          type: 'certificate_expiring',
          qualificationTypeId: cert.qualification_type_id || null,
          text: `${cert.name} expires in ${cert.days_remaining} days.`,
        });
      });
    }
    if (stats?.ppe?.problematic) {
      stats.ppe.problematic.forEach((item) => {
        const criticalIssue = item.issues.find(i => i.type.includes('expired')) || item.issues[0];
        dynamicNotifications.push({
          type: 'ppe_issue',
          text: `${item.name} - ${criticalIssue?.message || 'Requires attention'}`,
        });
      });
    }
    if (stats?.nominations?.pending > 0) {
      dynamicNotifications.push({
        type: 'nominations',
        text: `${stats.nominations.pending} nomination(s) require your attention.`,
      });
    }
    if (stats?.work_permits?.pending > 0) {
      dynamicNotifications.push({
        type: 'work_permits',
        text: `${stats.work_permits.pending} work permit(s) require your attention.`,
      });
    }
    setNotifications(dynamicNotifications);
  }, [stats]);

  const handleNotificationClick = (notification) => {
    switch (notification.type) {
      case 'certificate_expired':
      case 'certificate_expiring':
        // Deep link prosto do szczegółów kwalifikacji, nie do listy dokumentów
        navigate(
          notification.qualificationTypeId
            ? `/my-documents?qualificationTypeId=${notification.qualificationTypeId}`
            : '/my-documents'
        );
        break;
      case 'ppe_issue':
        navigate('/my-ppe');
        break;
      case 'nominations':
        navigate('/my-nominations?status=pending');
        break;
      case 'work_permits':
        navigate('/my-workpermits');
        break;
      default:
        break;
    }
  };

  // Unique missing qualifications across all roles, with role info
  const uniqueMissingQuals = React.useMemo(() => {
    if (!stats?.roles_compliance) return [];
    const map = new Map(); // qualification_type_id -> { ...mq, roles: [name, ...] }
    for (const role of stats.roles_compliance) {
      for (const mq of role.missing_qualifications || []) {
        if (map.has(mq.qualification_type_id)) {
          map.get(mq.qualification_type_id).roles.push(role.role_name);
        } else {
          map.set(mq.qualification_type_id, { ...mq, roles: [role.role_name] });
        }
      }
    }
    return Array.from(map.values());
  }, [stats?.roles_compliance]);

  // The four "what's next" cards are all views onto the same thing - the user's work
  // plan - so all four open it. Shared so a fifth card cannot quietly land elsewhere.
  const planningCardProps = {
    className: 'bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden '
      + 'cursor-pointer transition-all hover:shadow-md hover:border-brand-300',
    onClick: () => navigate('/my-planning'),
    title: 'Open My Work Planning',
    'data-planning-card': true,
  };

  const handleStatCardClick = () => {
    if (user?.role === 'userves') {
      navigate('/my-workpermits/orders');
    } else {
      navigate('/my-workpermits?status=planned_approved');
    }
  };

  return (
    <div className="h-full p-6 flex-1 flex flex-col overflow-auto">
      <main className="flex-1 overflow-x-hidden overflow-y-auto">
        <div className="container mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-black text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500">Welcome back, {user?.firstName || 'User'}</p>
          </div>

          {/* Stats grid - two rows of four */}
          <StatsGrid
            stats={stats}
            loadingStats={loadingStats}
            onNavigate={navigate}
            onStatCardClick={handleStatCardClick}
          />

          {/* Two Column Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Urgent Actions */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" style={{ minHeight: '280px' }}>
              <div className="bg-gradient-to-r from-red-50 to-white px-4 py-3 border-b border-red-100">
                <div className="flex items-center gap-2">
                  <TriangleAlert className="h-5 w-5 text-red-500" />
                  <h2 className="text-sm font-bold text-slate-900">Urgent Actions Required</h2>
                </div>
              </div>
              <div className="flex-1 p-4">
                {notifications.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {notifications.map((notification, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100 cursor-pointer hover:bg-red-100 transition-colors"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <TriangleAlert className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs font-medium text-red-800">{notification.text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-slate-500 bg-slate-50 rounded-lg p-4">
                    No urgent actions required at this time.
                  </div>
                )}
              </div>
            </div>

            {/* Missing Certificates */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" style={{ minHeight: '280px' }}>
              <div className="bg-gradient-to-r from-amber-50 to-white px-4 py-3 border-b border-amber-100">
                <div className="flex items-center gap-2">
                  <TriangleAlert className="h-5 w-5 text-amber-500" />
                  <h2 className="text-sm font-bold text-slate-900">Missing Certificates</h2>
                  {uniqueMissingQuals.length > 0 && (
                    <span className="ml-auto text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                      {uniqueMissingQuals.length} missing
                    </span>
                  )}
                </div>
              </div>
              <div className="flex-1 p-4">
                {uniqueMissingQuals.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {uniqueMissingQuals.map((mq) => (
                      <div
                        key={mq.qualification_type_id}
                        className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors"
                        onClick={() => navigate('/my-company-roles')}
                      >
                        <CircleX className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-slate-800 truncate">{mq.qualification_name}</p>
                          <p className="text-[10px] text-slate-500">
                            Required for: {mq.roles.length === 1 ? mq.roles[0] : 'Multiple roles'}
                            {mq.training_number ? ` · ${mq.training_number}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-emerald-600 bg-emerald-50 rounded-lg p-4 font-medium">
                    All required certificates are up to date.
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-brand-50 to-white px-4 py-3 border-b border-brand-100">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-brand-500" />
                  <h2 className="text-sm font-bold text-slate-900">Quick Actions</h2>
                </div>
              </div>
              <div className="p-3 space-y-1">
                <button
                  onClick={() => navigate('/my-nominations?status=pending')}
                  className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center mr-3">
                      <FileText className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-bold text-slate-700 group-hover:text-brand-600">Sign Nominations</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-brand-500" />
                </button>
                <button
                  onClick={() => navigate('/my-workpermits/create')}
                  className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center mr-3">
                      <Send className="h-4 w-4 text-emerald-600" />
                    </div>
                    <span className="text-sm font-bold text-slate-700 group-hover:text-brand-600">Create Work Permit</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-brand-500" />
                </button>
                <button
                  onClick={() => navigate('/my-documents')}
                  className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center mr-3">
                      <FilePlus className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-bold text-slate-700 group-hover:text-brand-600">My Documents</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-brand-500" />
                </button>
                <button
                  onClick={() => navigate('/my-ppe')}
                  className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center mr-3">
                      <Shield className="h-4 w-4 text-indigo-600" />
                    </div>
                    <span className="text-sm font-bold text-slate-700 group-hover:text-brand-600">My PPE</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-brand-500" />
                </button>
              </div>
            </div>
          </div>

          {/* Project, Travel, Accommodation, Training - all four open the work plan */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Next Project */}
            <div {...planningCardProps}>
              <div className="bg-gradient-to-r from-blue-50 to-white px-4 py-3 border-b border-blue-100">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-500" />
                  <h2 className="text-sm font-bold text-slate-900">
                    {stats?.nearest_project?.is_ongoing ? 'Current Project' : 'Next Project'}
                  </h2>
                </div>
              </div>
              <div className="p-4">
                {loadingStats ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  </div>
                ) : stats?.nearest_project ? (
                  <div className="space-y-2">
                    <p className="text-base font-bold text-slate-900">{stats.nearest_project.project_name}</p>
                    {stats.nearest_project.project_country && (
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {stats.nearest_project.project_country}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        {new Date(stats.nearest_project.start_date).toLocaleDateString()} - {new Date(stats.nearest_project.end_date).toLocaleDateString()}
                      </span>
                    </div>
                    {stats.nearest_project.is_ongoing && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Currently Active
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-slate-400">
                    No upcoming projects assigned
                  </div>
                )}
              </div>
            </div>

            {/* Next Travel */}
            <div {...planningCardProps}>
              <div className="bg-gradient-to-r from-amber-50 to-white px-4 py-3 border-b border-amber-100">
                <div className="flex items-center gap-2">
                  <Plane className="h-5 w-5 text-amber-500" />
                  <h2 className="text-sm font-bold text-slate-900">
                    {stats?.nearest_travel?.is_ongoing ? "Today's Travel" : 'Next Travel'}
                  </h2>
                </div>
              </div>
              <div className="p-4">
                {loadingStats ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-500"></div>
                  </div>
                ) : stats?.nearest_travel ? (
                  <div className="space-y-2">
                    {/* Minimal: "to/from [project]" header + date + transport badge.
                        Richer trip details live in PlanningWork (calendar). */}
                    <p className="text-base font-bold text-slate-900">
                      <span className="text-slate-400 font-normal lowercase mr-1">
                        {stats.nearest_travel.travel_type === 'check_in' ? 'to'
                          : stats.nearest_travel.travel_type === 'check_out' ? 'from' : ''}
                      </span>
                      {stats.nearest_travel.project_name || stats.nearest_travel.training_name || 'Travel'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{new Date(stats.nearest_travel.start_date).toLocaleDateString()}</span>
                    </div>
                    {stats.nearest_travel.transport_type === 'notplanning' ? (
                      <p className="text-xs text-amber-600 italic">⏳ Travel details not yet planned</p>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        stats.nearest_travel.transport_type === 'plane' ? 'bg-sky-100 text-sky-800' :
                        stats.nearest_travel.transport_type === 'car' ? 'bg-slate-100 text-slate-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {stats.nearest_travel.transport_type === 'plane' ? '✈️ Plane' :
                         stats.nearest_travel.transport_type === 'car' ? '🚗 Company Car' :
                         '🚘 Own Transport'}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-slate-400">
                    No upcoming travels scheduled
                  </div>
                )}
              </div>
            </div>

            {/* Next Accommodation */}
            <div {...planningCardProps}>
              <div className="bg-gradient-to-r from-purple-50 to-white px-4 py-3 border-b border-purple-100">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-purple-500" />
                  <h2 className="text-sm font-bold text-slate-900">
                    {stats?.nearest_accommodation?.is_ongoing ? 'Current Accommodation' : 'Next Accommodation'}
                  </h2>
                </div>
              </div>
              <div className="p-4">
                {loadingStats ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
                  </div>
                ) : stats?.nearest_accommodation ? (
                  <div className="space-y-2">
                    <p className="text-base font-bold text-slate-900">{stats.nearest_accommodation.accommodation_name}</p>
                    {stats.nearest_accommodation.accommodation_city && (
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {stats.nearest_accommodation.accommodation_city}
                      </p>
                    )}
                    <p className="text-xs text-slate-600">
                      Room: <span className="font-medium">{stats.nearest_accommodation.room_number}</span>
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        {new Date(stats.nearest_accommodation.start_date).toLocaleDateString()} - {new Date(stats.nearest_accommodation.end_date).toLocaleDateString()}
                      </span>
                    </div>
                    {stats.nearest_accommodation.is_ongoing && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Currently Staying
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-slate-400">
                    No upcoming accommodation booked
                  </div>
                )}
              </div>
            </div>

            {/* Next Training */}
            <div {...planningCardProps}>
              <div className="bg-gradient-to-r from-emerald-50 to-white px-4 py-3 border-b border-emerald-100">
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-emerald-500" />
                  <h2 className="text-sm font-bold text-slate-900">
                    {stats?.nearest_training?.is_ongoing ? 'Current Training' : 'Next Training'}
                  </h2>
                </div>
              </div>
              <div className="p-4">
                {loadingStats ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-500"></div>
                  </div>
                ) : stats?.nearest_training ? (
                  <div className="space-y-2">
                    <p className="text-base font-bold text-slate-900">{stats.nearest_training.training_name}</p>
                    {stats.nearest_training.training_center_name && (
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {stats.nearest_training.training_center_name}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        {new Date(stats.nearest_training.start_date).toLocaleDateString()} - {new Date(stats.nearest_training.end_date).toLocaleDateString()}
                      </span>
                    </div>
                    {stats.nearest_training.is_ongoing ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Currently Ongoing
                      </span>
                    ) : stats.nearest_training.status === 'pre_planned' && (
                      // A reserved date with no course yet - say so, rather than showing a
                      // bare "Training" and letting the user assume it is booked.
                      <p className="text-xs text-amber-600 italic">⏳ Date reserved, course not chosen yet</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-slate-400">
                    No upcoming trainings scheduled
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Main UnifiedDashboard Component
const UnifiedDashboard = () => {
  const { user } = useContext(UserContext);
  const outletContext = useOutletContext();
  const viewMode = outletContext?.viewMode || 'admin';

  // Check if user is admin
  const isAdmin = ['admin', 'adminves', 'superuser'].includes(user?.role);

  // Render appropriate dashboard based on role and viewMode
  if (isAdmin) {
    // Admin może przełączać między widokiem admina i usera
    if (viewMode === 'user') {
      return <UserDashboardContent viewMode="user" />;
    }
    return <AdminDashboard />;
  }

  return <UserDashboardContent />;
};

export default UnifiedDashboard;
